import { HonorariosCob } from './HonorariosCob'
import { useState, useEffect, useCallback, useMemo, useRef, Fragment } from 'react'
import { useAuth } from '../contexts/AuthContext'
import ConfirmDialog from './ConfirmDialog'
import {
  FiRefreshCw,
  FiFileText,
  FiAlertCircle,
  FiSearch,
  FiChevronDown,
  FiChevronUp,
  FiChevronRight,
  FiSend,
  FiCheck,
  FiX,
  FiCheckSquare,
  FiLoader,
  FiBarChart2,
  FiCopy,
  FiClock,
  FiMessageCircle,
} from 'react-icons/fi'
import { LuBanknote } from 'react-icons/lu'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LabelList, AreaChart, Area
} from 'recharts'
import { MONEY } from '../lib/chartTheme'
import { NeuCheck } from './NeuCheck'
import { PlnKpis, PlnKpi, PlnBarra, PlnBarraRot, PlnBarraSep, PlnBloco } from './PanelShell'

// Painel Cobrança — movido do crm-twms pro fin.twms (2026-07-06), só a TELA.
// Os endpoints /api/cobranca/* são um proxy fino (backend/code/routes/cobranca.cjs)
// pro crm-twms-backend, que continua dono do Oracle DRACO, do envio WhatsApp/Meta
// (device "Faturamento") e do histórico de mensagens — nada disso migrou. "Ver
// conversa WhatsApp" abre o crm.twms.com.br numa aba nova via handoff SSO (mesmo
// token, mesmo mecanismo do EnvSwitcher) — o inbox de conversas continua lá.

interface Boleto {
  cliente: string; nome: string; tipo: string; grupo_codigo: string; grupo: string
  distrito: number; documento: string; serie: string; semana: number
  vencimento: string; emissao: string; valor: number; valor_pago: number
  debitos: number; creditos: number; saldo: number; dias_atraso: number
  cobrado?: boolean
  cobradoEm?: string | null    // ISO timestamp da última cobrança enviada
  cobrancasCount?: number       // Quantas cobranças já foram disparadas pra esse boleto
  respondeu?: boolean           // Cliente respondeu APÓS a última cobrança (= engajado)
  respostaEm?: string | null    // ISO timestamp da última resposta do cliente
}

interface DashboardData {
  boletos: Boleto[]
  summary: { totalSaldo: number; totalBoletos: number; totalClientes: number; ticketMedio: number }
  porFaixa: { faixa: string; quantidade: number; valor: number }[]
  porGrupo: { grupo: string; quantidade: number; valor: number }[]
  porSemana: { semana: number; quantidade: number; valor: number }[]
  timestamp: string
}

interface EfetividadeData {
  janela: number
  disparos: number
  disparosBrutos: number
  invalidos: number
  kpis: {
    conversao: number
    pagoApos: number
    pagoAntes: number
    semPagto: number
    parcial: number
    valorRecuperado: number
    delayMediano: number | null
    delayMedio: number | null
    desperdicio: number
  }
  buckets: { '0-3': number; '4-7': number; '8-15': number; '16+': number }
  recuperados?: {
    documento: number; cliente: number | null; nome: string | null; valorPago: number
    saldoRestante: number; pagoEm: string; disparoEm: string; dias: number; parcial: boolean; template: string
  }[]
  intencoes?: {
    documento: number; cliente: number | null; nome: string | null; valorCobrado: number
    disparoEm: string; respostaEm: string; respostas: number
    tipo: 'comprovante' | 'informou_pagamento' | 'quer_pagar' | 'respondeu'
    valorMencionado: number | null; resumo: string; conversationId: string | null
  }[]
  porTemplate: { template: string; disparos: number; pagoApos: number; valorRecuperado: number; conversao: number }[]
  timestamp: string
}

// Dossier do cliente carregado no right-pane.
interface DossierData {
  cliente: {
    codigo: string; nome: string; tipo: string; tipoLabel: string | null
    situacao: string | null; semLancamento: number
    grupo: string | null; grupoCodigo: string | null
    distrito: string | null; cidade: string | null
    cep: string | null; logradouro: string | null; numero: string | null; bairro: string | null
    email: string | null; cpf: string | null
    telefone: string | null; telefoneFormatado: string | null
  }
  conversationId: string | null
  conversationDeviceId: string | null  // device do deep link — Atendimento vs Faturamento
  funnelStatus: { status: string; title: string; color: string | null; updatedAt: string } | null
  cobrancas: Array<{
    messageId: string | null; timestamp: string
    documento: string | null; valor: number | null; template: string | null
    status: string // sent | delivered | read | failed
    conversationId: string | null; deviceId: string | null  // de qual conv/device veio
    resposta: { content: string; timestamp: string } | null
  }>
  // Pagamentos recentes (DOCCLIENTES quitados/parciais) — espelha o painel externo.
  pagamentos?: Array<{
    documento: string; serie: string
    vencimento: string | null; ultPagamento: string | null
    valor: number; valorPago: number; saldo: number; quitado: boolean
  }>
  pagamentosTotal?: number
}

// Cooldown em horas pra reenvio do mesmo boleto. Backend também enforce —
// este valor é só pra UX (desabilitar botão + tooltip).
// 168h = 7 dias — alinha com a janela de baixa bancária do DRACO (D+3/D+7).
const COBRANCA_COOLDOWN_HOURS = 168

// Cap máximo de contatos por batch. Deve casar com COBRANCA_BATCH_CAP no backend
// (crm-twms server.cjs, do outro lado do proxy). Frontend bloqueia clique pra
// evitar 400 do servidor com seleção grande — feedback imediato pro operador.
const COBRANCA_BATCH_CAP = 500

// Send results persistem entre F5/troca de aba via localStorage — operador pode
// disparar pela manhã, voltar à tarde e ainda ver "X enviadas". Auto-descarte
// quando passa de RESULTS_STORAGE_MAX_AGE_MS.
const RESULTS_STORAGE_KEY = 'cobranca_send_results_v1'
const RESULTS_STORAGE_MAX_AGE_MS = 24 * 60 * 60 * 1000

type FaixaKey = '1-7' | '8-30' | '31-60' | '60+'
type StatusKey = 'pendente' | 'cobrado' | 'cooldown'
const FAIXA_OPTIONS: { key: FaixaKey; label: string }[] = [
  { key: '1-7', label: '1-7d' },
  { key: '8-30', label: '8-30d' },
  { key: '31-60', label: '31-60d' },
  { key: '60+', label: '60+' },
]
const STATUS_OPTIONS: { key: StatusKey; label: string }[] = [
  { key: 'pendente', label: 'Pendentes' },
  { key: 'cobrado',  label: 'Cobrados' },
  { key: 'cooldown', label: 'Em cooldown' },
]

function formatCurrency(v: number) { return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) }
function formatDate(d: string) { if (!d) return '-'; const [y, m, dd] = d.split('-'); return `${dd}/${m}/${y}` }
// CPF (11 díg.) → XXX.XXX.XXX-XX; se vier com tamanho inesperado, devolve cru.
function formatCpf(cpf: string | null): string | null {
  if (!cpf) return null
  const d = cpf.replace(/\D/g, '')
  return d.length === 11 ? `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}` : cpf
}

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null
  return (
    <div className="glass-popover rounded-lg p-2 text-xs">
      <p className="font-medium text-foreground mb-1">{label}</p>
      {payload.map((e: any, i: number) => (
        <p key={i} style={{ color: e.color }}>{e.name}: {e.name === 'valor' ? formatCurrency(e.value) : e.value}</p>
      ))}
    </div>
  )
}

// onOpenConversation: navega pro painel Conversas local (view 'conversas' do
// App) auto-selecionando a conversa do cliente — substituiu o handoff SSO que
// abria o crm.twms.com.br numa aba nova (o inbox do Faturamento migrou pra cá).
export function CobrancaPanel({ onOpenConversation }: { onOpenConversation?: (conversationId: string) => void }) {
  const { authenticatedFetch } = useAuth()

  const [data, setData] = useState<DashboardData | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [sortField, setSortField] = useState<'dias_atraso' | 'saldo' | 'nome'>('dias_atraso')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')

  // Filtros estruturais (chips). Set vazio = "todos".
  const [filterFaixa, setFilterFaixa] = useState<Set<FaixaKey>>(new Set())
  const [filterStatus, setFilterStatus] = useState<Set<StatusKey>>(new Set())
  const [filterGrupo, setFilterGrupo] = useState<string | null>(null)

  // Charts colapsáveis — default fechado (foco operacional na tabela).
  const [chartsExpanded, setChartsExpanded] = useState(false)
  // Send results persistentes (visível enquanto não dispensado).
  const [resultsExpanded, setResultsExpanded] = useState(true)

  // Disparo
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [sending, setSending] = useState(false)
  // Trava síncrona pro botão Confirmar do modal. useState do `sending` é async (só
  // vira true no próximo render), então 2 cliques rapidíssimos passam ambos pelo
  // check. useRef muta SYNCHRONOUSLY no mesmo tick — barreira de verdade.
  const confirmingRef = useRef(false)
  // Sumário e detalhes do disparo — restaurados do localStorage no mount.
  const [sendProgress, setSendProgress] = useState<{ sent: number; errors: number; total: number } | null>(() => {
    try {
      const raw = localStorage.getItem(RESULTS_STORAGE_KEY)
      if (!raw) return null
      const parsed = JSON.parse(raw)
      if (!parsed?.savedAt || Date.now() - parsed.savedAt > RESULTS_STORAGE_MAX_AGE_MS) return null
      return parsed.progress || null
    } catch { return null }
  })
  const [sendResults, setSendResults] = useState<any[] | null>(() => {
    try {
      const raw = localStorage.getItem(RESULTS_STORAGE_KEY)
      if (!raw) return null
      const parsed = JSON.parse(raw)
      if (!parsed?.savedAt || Date.now() - parsed.savedAt > RESULTS_STORAGE_MAX_AGE_MS) return null
      return parsed.results || null
    } catch { return null }
  })
  const [loadingPhones, setLoadingPhones] = useState(false)

  // Persiste send results em localStorage — sobrevive F5 e troca de aba até 24h.
  useEffect(() => {
    try {
      if (sendResults && sendProgress) {
        localStorage.setItem(RESULTS_STORAGE_KEY, JSON.stringify({
          savedAt: Date.now(), progress: sendProgress, results: sendResults,
        }))
      } else {
        localStorage.removeItem(RESULTS_STORAGE_KEY)
      }
    } catch { /* storage cheio ou bloqueado — segue sem persistir */ }
  }, [sendResults, sendProgress])
  // Reenvio individual: doc do boleto em curso (botão da linha vira spinner)
  const [resendingDoc, setResendingDoc] = useState<string | null>(null)
  // Modal de confirmação — pode ser de batch ou de reenvio individual.
  const [confirmAction, setConfirmAction] = useState<
    | { type: 'batch' }
    | { type: 'resend'; boleto: Boleto }
    | null
  >(null)

  // Master-detail: dossier do cliente clicado.
  const [dossierContext, setDossierContext] = useState<{ codigo: string; activeDoc: string | null } | null>(null)
  const [dossierData, setDossierData] = useState<DossierData | null>(null)
  const [dossierLoading, setDossierLoading] = useState(false)
  const [dossierError, setDossierError] = useState<string | null>(null)

  useEffect(() => {
    if (!dossierContext) { setDossierData(null); setDossierError(null); return }
    let cancelled = false
    const fetchDossier = async () => {
      setDossierLoading(true); setDossierError(null)
      try {
        const r = await authenticatedFetch(`/api/cobranca/cliente/${encodeURIComponent(dossierContext.codigo)}`)
        if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.error || `Erro ${r.status}`) }
        const d = await r.json()
        if (!cancelled) setDossierData(d)
      } catch (err) {
        if (!cancelled) setDossierError(err instanceof Error ? err.message : 'Erro')
      } finally {
        if (!cancelled) setDossierLoading(false)
      }
    }
    fetchDossier()
    return () => { cancelled = true }
  }, [dossierContext, authenticatedFetch])

  // Boletos abertos do cliente do dossier (puxa do data já carregado — instantâneo).
  const dossierBoletos = useMemo(() => {
    if (!dossierContext || !data) return []
    return data.boletos.filter(b => b.cliente === dossierContext.codigo)
  }, [dossierContext, data])
  const dossierTotalAberto = dossierBoletos.reduce((s, b) => s + b.saldo, 0)

  const openDossier = (b: Boleto) => setDossierContext({ codigo: b.cliente, activeDoc: b.documento })
  const closeDossier = () => setDossierContext(null)

  const loadDashboard = useCallback(async () => {
    setIsLoading(true); setError(null)
    try {
      const res = await authenticatedFetch('/api/cobranca/dashboard')
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || `Erro ${res.status}`) }
      setData(await res.json())
    } catch (err) { setError(err instanceof Error ? err.message : 'Erro') }
    finally { setIsLoading(false) }
  }, [authenticatedFetch])

  useEffect(() => { loadDashboard() }, [loadDashboard])

  // ── Efetividade da cobrança (cruzamento WhatsApp x pagamentos DRACO) ──
  // Janela default 7d alinha com o D+7 das baixas bancárias do DRACO; 3d
  // captura o pico do efeito (mediana de pagamento ~3 dias após disparo);
  // 30/60/90 dão visão histórica e estabilizam a taxa.
  const efetividadeJanelas: Array<{ key: number; label: string }> = [
    { key: 3, label: '3d' },
    { key: 7, label: '7d' },
    { key: 30, label: '30d' },
    { key: 60, label: '60d' },
    { key: 90, label: '90d' },
  ]
  const [efetividadeJanela, setEfetividadeJanela] = useState<number>(7)
  const [efetividade, setEfetividade] = useState<EfetividadeData | null>(null)
  const [efetividadeLoading, setEfetividadeLoading] = useState(false)
  const [efetividadeError, setEfetividadeError] = useState<string | null>(null)

  useEffect(() => {
    // Só consulta quando o painel de Análises está aberto — evita 1 query
    // Oracle a cada mount de quem só vai disparar cobrança.
    if (!chartsExpanded) return
    let cancelled = false
    const load = async () => {
      setEfetividadeLoading(true); setEfetividadeError(null)
      try {
        const r = await authenticatedFetch(`/api/cobranca/efetividade?janela=${efetividadeJanela}`)
        if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.error || `Erro ${r.status}`) }
        const d = await r.json()
        if (!cancelled) setEfetividade(d)
      } catch (err) {
        if (!cancelled) setEfetividadeError(err instanceof Error ? err.message : 'Erro')
      } finally {
        if (!cancelled) setEfetividadeLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [chartsExpanded, efetividadeJanela, authenticatedFetch])

  // Template default vem do backend (system_settings.cobranca_template_name).
  // Fallback pro nome atual aprovado caso a chamada falhe no boot.
  const [cobrancaTemplate, setCobrancaTemplate] = useState('cobranca_previsao_tupperware')
  const COBRANCA_TEMPLATE_LANG = 'pt_BR'

  useEffect(() => {
    let cancelled = false
    authenticatedFetch('/api/cobranca/templates')
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (!cancelled && d?.defaultTemplateName) setCobrancaTemplate(d.defaultTemplateName) })
      .catch(() => {})
    return () => { cancelled = true }
  }, [authenticatedFetch])

  // True se o boleto teve cobrança recente (dentro do cooldown). Helper precisa
  // ser declarada ANTES do useMemo de filteredBoletos pra evitar TDZ.
  const isCooldownActive = (b: Boleto): boolean => {
    if (!b.cobradoEm) return false
    const elapsed = Date.now() - new Date(b.cobradoEm).getTime()
    return elapsed < COBRANCA_COOLDOWN_HOURS * 60 * 60 * 1000
  }

  const cooldownRemainingLabel = (b: Boleto): string => {
    if (!b.cobradoEm) return ''
    const remainingMs = COBRANCA_COOLDOWN_HOURS * 60 * 60 * 1000 - (Date.now() - new Date(b.cobradoEm).getTime())
    if (remainingMs <= 0) return ''
    const h = Math.ceil(remainingMs / (60 * 60 * 1000))
    if (h >= 48) return `${Math.ceil(h / 24)}d`
    return h <= 1 ? '1h' : `${h}h`
  }

  // Grupos únicos pra dropdown — derivados do dataset.
  // Ranking pro gráfico "Por Grupo (valor)": top 7 em barras horizontais.
  // "Outros" (agregado) NÃO vira barra — dominava a escala e esmagava os
  // grupos reais; vai como texto no cabeçalho do card.
  const porGrupoBars = useMemo(() => {
    const arr = [...(data?.porGrupo || [])].map(g => ({ ...g, valor: Number(g.valor) || 0 }))
    let outros = 0
    const i = arr.findIndex(g => /^outros$/i.test(String(g.grupo).trim()))
    if (i >= 0) outros += arr.splice(i, 1)[0].valor
    arr.sort((a, b) => b.valor - a.valor)
    const top = arr.slice(0, 7)
    outros += arr.slice(7).reduce((s, g) => s + g.valor, 0)
    return { top, outros }
  }, [data])

  const uniqueGrupos = useMemo(() => {
    if (!data) return []
    return Array.from(new Set(data.boletos.map(b => b.grupo).filter(Boolean))).sort()
  }, [data])

  const filteredBoletos = useMemo(() => {
    if (!data) return []
    let list = data.boletos
    // Filtro Faixa (OR dentro do grupo)
    if (filterFaixa.size > 0) {
      list = list.filter(b => {
        const d = b.dias_atraso
        return (filterFaixa.has('1-7')   && d >= 1  && d <= 7)  ||
               (filterFaixa.has('8-30')  && d >= 8  && d <= 30) ||
               (filterFaixa.has('31-60') && d >= 31 && d <= 60) ||
               (filterFaixa.has('60+')   && d >= 61)
      })
    }
    // Filtro Status (pendente / cobrado / em cooldown — mutuamente exclusivos por boleto)
    if (filterStatus.size > 0) {
      list = list.filter(b => {
        const cool = isCooldownActive(b)
        if (filterStatus.has('pendente') && !b.cobrado) return true
        if (filterStatus.has('cobrado')  && b.cobrado && !cool) return true
        if (filterStatus.has('cooldown') && b.cobrado && cool) return true
        return false
      })
    }
    // Filtro Grupo (single-select)
    if (filterGrupo) list = list.filter(b => b.grupo === filterGrupo)
    // Busca textual
    if (searchTerm) {
      const t = searchTerm.toLowerCase()
      list = list.filter(b => b.nome.toLowerCase().includes(t) || b.cliente.toString().includes(t) || b.grupo.toLowerCase().includes(t) || b.documento.toString().includes(t))
    }
    return [...list].sort((a, b) => {
      const av = a[sortField], bv = b[sortField]
      if (typeof av === 'string') return sortDir === 'asc' ? av.localeCompare(bv as string) : (bv as string).localeCompare(av)
      return sortDir === 'asc' ? (av as number) - (bv as number) : (bv as number) - (av as number)
    })
  }, [data, filterFaixa, filterStatus, filterGrupo, searchTerm, sortField, sortDir])

  const handleSort = (f: typeof sortField) => { if (sortField === f) setSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setSortField(f); setSortDir(f === 'dias_atraso' ? 'asc' : 'desc') } }
  const SortIcon = ({ field }: { field: typeof sortField }) => sortField !== field ? null : sortDir === 'asc' ? <FiChevronUp className="w-3 h-3" /> : <FiChevronDown className="w-3 h-3" />

  const bKey = (b: Boleto) => `${b.cliente}|${b.documento}|${b.serie}`
  const toggleSelect = (b: Boleto) => { const k = bKey(b); setSelected(p => { const n = new Set(p); n.has(k) ? n.delete(k) : n.add(k); return n }) }
  const selectAll = () => { selected.size === filteredBoletos.length ? setSelected(new Set()) : setSelected(new Set(filteredBoletos.map(bKey))) }

  // ── Agrupar por cliente (toggle flat ⇄ agrupado). Default agrupado/EXPANDIDO. ──
  // Só clientes com 2+ boletos viram grupo recolhível; 1 boleto = linha normal
  // (95% dos casos — agrupar e recolher esconderia o doc/Reenviar à toa). Rastreia
  // os RECOLHIDOS (Set vazio = tudo expandido) pra "começar expandido" sem precisar
  // conhecer a lista de clientes no boot.
  const [viewMode, setViewMode] = useState<'grouped' | 'flat'>('grouped')
  const [collapsedClients, setCollapsedClients] = useState<Set<string>>(new Set())
  const toggleExpanded = (cliente: string) => setCollapsedClients(p => { const n = new Set(p); n.has(cliente) ? n.delete(cliente) : n.add(cliente); return n })

  // Agrupa filteredBoletos por cliente; agrega saldo/atraso/venc; ordena pelos
  // agregados respeitando sortField/sortDir. Portado de cobranca-externa/Painel.jsx.
  const groupedBoletos = useMemo(() => {
    const map = new Map<string, { cliente: string; nome: string; grupo: string; boletos: Boleto[] }>()
    for (const b of filteredBoletos) {
      if (!map.has(b.cliente)) map.set(b.cliente, { cliente: b.cliente, nome: b.nome, grupo: b.grupo, boletos: [] })
      map.get(b.cliente)!.boletos.push(b)
    }
    const arr = [...map.values()].map(g => {
      const saldo = g.boletos.reduce((s, b) => s + (b.saldo || 0), 0)
      const maxAtraso = g.boletos.reduce((m, b) => Math.max(m, b.dias_atraso || 0), 0)
      const minVenc = g.boletos.reduce<string | null>((m, b) => (b.vencimento && (!m || b.vencimento < m)) ? b.vencimento : m, null)
      return { ...g, count: g.boletos.length, saldo, maxAtraso, minVenc }
    })
    arr.sort((a, b) => {
      let av: number | string, bv: number | string
      if (sortField === 'nome') { av = a.nome || ''; bv = b.nome || '' }
      else if (sortField === 'saldo') { av = a.saldo; bv = b.saldo }
      else { av = a.maxAtraso; bv = b.maxAtraso } // dias_atraso → pior atraso do grupo
      if (typeof av === 'string') return sortDir === 'asc' ? av.localeCompare(bv as string) : (bv as string).localeCompare(av)
      return sortDir === 'asc' ? (av as number) - (bv as number) : (bv as number) - (av as number)
    })
    return arr
  }, [filteredBoletos, sortField, sortDir])

  // Grupos = só clientes com 2+ boletos (os recolhíveis).
  const multiGroups = useMemo(() => groupedBoletos.filter(g => g.count >= 2), [groupedBoletos])
  const allExpanded = multiGroups.every(g => !collapsedClients.has(g.cliente))
  const expandAll = () => setCollapsedClients(new Set())
  const collapseAll = () => setCollapsedClients(new Set(multiGroups.map(g => g.cliente)))
  // Seleciona/desmarca todos os boletos de um grupo (mantém seleção por-boleto).
  const toggleGroupSelect = (boletos: Boleto[]) => {
    const keys = boletos.map(bKey)
    const allSel = keys.every(k => selected.has(k))
    setSelected(p => { const n = new Set(p); keys.forEach(k => allSel ? n.delete(k) : n.add(k)); return n })
  }
  const selectPending = () => { const pending = filteredBoletos.filter(b => !b.cobrado); setSelected(new Set(pending.map(bKey))) }
  // "Selecionar reenvio" pula boletos em cooldown — operador não fica com seleção
  // que vai ser bloqueada pelo backend ao clicar Reenviar.
  const selectCobrados = () => {
    const cob = filteredBoletos.filter(b => b.cobrado && !isCooldownActive(b))
    setSelected(new Set(cob.map(bKey)))
  }

  // Toggles dos filtros chips
  const toggleFaixa  = (k: FaixaKey)  => setFilterFaixa(p  => { const n = new Set(p); n.has(k) ? n.delete(k) : n.add(k); return n })
  const toggleStatus = (k: StatusKey) => setFilterStatus(p => { const n = new Set(p); n.has(k) ? n.delete(k) : n.add(k); return n })
  const clearFilters = () => { setFilterFaixa(new Set()); setFilterStatus(new Set()); setFilterGrupo(null); setSearchTerm('') }
  const hasActiveFilters = filterFaixa.size > 0 || filterStatus.size > 0 || filterGrupo !== null || searchTerm.trim() !== ''

  // Wrapper que abre modal de confirmação antes do envio. handleSendConfirmed
  // executa a ação real só depois do clique no modal.
  const handleSend = () => {
    if (selected.size === 0 || sending || resendingDoc) return
    setConfirmAction({ type: 'batch' })
  }

  const handleSendConfirmed = async () => {
    if (selected.size === 0) return
    const boletosToSend = filteredBoletos.filter(b => selected.has(bKey(b)))
    const clienteMap = new Map<string, Boleto>()
    for (const b of boletosToSend) { const ex = clienteMap.get(b.cliente); if (!ex || b.saldo > ex.saldo) clienteMap.set(b.cliente, b) }
    const uniqueClientes = Array.from(clienteMap.values())
    setSending(true); setSendResults(null); setSendProgress({ sent: 0, errors: 0, total: uniqueClientes.length })
    setResultsExpanded(true)
    try {
      // Buscar telefones (timeout 30s)
      setLoadingPhones(true)
      const telController = new AbortController()
      const telTimeout = setTimeout(() => telController.abort(), 30000)
      const telRes = await authenticatedFetch('/api/cobranca/telefones', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ codigos: uniqueClientes.map(b => b.cliente) }), signal: telController.signal })
      clearTimeout(telTimeout)
      const phones: Record<string, string> = (await telRes.json()).telefones || {}
      setLoadingPhones(false)
      const contatos = uniqueClientes.filter(b => phones[b.cliente]).map(b => ({ cliente: b.cliente, nome: b.nome, telefone: phones[b.cliente], documento: b.documento, serie: b.serie, saldo: b.saldo, vencimento: formatDate(b.vencimento), dias_atraso: b.dias_atraso }))
      const semTel = uniqueClientes.filter(b => !phones[b.cliente])

      // Enviar cobranças (timeout 5min — ~500ms por contato)
      const envController = new AbortController()
      const envTimeout = setTimeout(() => envController.abort(), 300000)
      // timeout explícito: o default do authenticatedFetch é 30s e vence o signal
      // externo — batch de N contatos leva ~500ms×N no servidor (499 com 57 contatos, 09/07).
      const envRes = await authenticatedFetch('/api/cobranca/enviar', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ contatos, templateName: cobrancaTemplate, templateLanguage: COBRANCA_TEMPLATE_LANG }), signal: envController.signal, timeout: 300000 } as RequestInit)
      clearTimeout(envTimeout)
      const envData = await envRes.json()
      if (!envRes.ok) {
        // 409 = lock concorrente no servidor (outra request em andamento)
        setError(envData.error || `Erro ${envRes.status} ao disparar`)
        return
      }
      setSendResults([...(envData.results || []), ...semTel.map(b => ({ cliente: b.cliente, nome: b.nome, status: 'error', error: 'Sem telefone' }))])
      setSendProgress({ sent: envData.sent || 0, errors: (envData.errors || 0) + semTel.length, total: uniqueClientes.length })
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erro ao enviar'
      if (msg.includes('abort')) setError('Timeout — o envio pode estar em andamento no servidor. Aguarde e atualize.')
      else setError(msg)
    }
    finally { setSending(false) }
  }

  // Reenvio de UM boleto — abre modal primeiro, executa em handleResendConfirmed.
  const handleResend = (b: Boleto) => {
    if (resendingDoc || sending) return
    if (isCooldownActive(b)) return // botão já estaria disabled, defesa em profundidade
    setConfirmAction({ type: 'resend', boleto: b })
  }

  // Pipeline do reenvio (telefones → enviar) — bate na mesma rota do batch
  // mas com 1 item só. resendingDoc bloqueia outros reenvios E o batch em paralelo.
  const handleResendConfirmed = async (b: Boleto) => {
    setResendingDoc(b.documento)
    setResultsExpanded(true)
    try {
      const telRes = await authenticatedFetch('/api/cobranca/telefones', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ codigos: [b.cliente] })
      })
      const phones: Record<string, string> = (await telRes.json()).telefones || {}
      const telefone = phones[b.cliente]
      if (!telefone) {
        setSendResults(prev => [...(prev || []), { cliente: b.cliente, nome: b.nome, status: 'error', error: 'Sem telefone' }])
        return
      }
      const contato = {
        cliente: b.cliente, nome: b.nome, telefone,
        documento: b.documento, serie: b.serie, saldo: b.saldo,
        vencimento: formatDate(b.vencimento), dias_atraso: b.dias_atraso,
      }
      const envRes = await authenticatedFetch('/api/cobranca/enviar', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contatos: [contato], templateName: cobrancaTemplate, templateLanguage: COBRANCA_TEMPLATE_LANG }),
        // revalidação Oracle no servidor pode passar dos 30s default do wrapper
        timeout: 120000,
      } as RequestInit)
      const envData = await envRes.json()
      if (!envRes.ok) {
        setSendResults(prev => [...(prev || []), { cliente: b.cliente, nome: b.nome, status: 'error', error: envData.error || `Erro ${envRes.status}` }])
        return
      }
      const result = envData.results?.[0] || { cliente: b.cliente, status: 'error', error: 'Resposta vazia' }
      setSendResults(prev => [...(prev || []), result])
      // Atualiza local cobradoEm + count sem refresh full (snappy)
      if (result.status === 'sent' && data) {
        setData(prev => prev ? {
          ...prev,
          boletos: prev.boletos.map(x => x.documento === b.documento
            ? { ...x, cobrado: true, cobradoEm: new Date().toISOString(), cobrancasCount: (x.cobrancasCount || 0) + 1 }
            : x)
        } : prev)
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erro ao reenviar'
      setSendResults(prev => [...(prev || []), { cliente: b.cliente, nome: b.nome, status: 'error', error: msg }])
    } finally {
      setResendingDoc(null)
    }
  }

  // "Há X" relativo curto pra tooltip — evita dependência do date-fns.
  const formatRelativeDays = (iso: string): string => {
    const ms = Date.now() - new Date(iso).getTime()
    const days = Math.floor(ms / (1000 * 60 * 60 * 24))
    if (days === 0) return 'hoje'
    if (days === 1) return 'ontem'
    if (days < 30) return `há ${days} dias`
    const months = Math.floor(days / 30)
    return months === 1 ? 'há 1 mês' : `há ${months} meses`
  }

  const selectedBoletos = filteredBoletos.filter(b => selected.has(bKey(b)))
  const selectedTotal = selectedBoletos.reduce((s, b) => s + b.saldo, 0)
  const selectedClientes = new Set(selectedBoletos.map(b => b.cliente)).size
  // Quando a seleção é homogênea (todos pendentes OU todos cobrados), o label
  // do CTA reflete a ação. Misturado fica "Enviar" genérico — o backend cobra
  // os dois casos pelo mesmo endpoint, apenas a comunicação visual muda.
  const selectionAllCobrados = selectedBoletos.length > 0 && selectedBoletos.every(b => b.cobrado)
  const selectionAllPending  = selectedBoletos.length > 0 && selectedBoletos.every(b => !b.cobrado)
  const sendLabel = selectionAllCobrados ? 'Reenviar' : selectionAllPending ? 'Cobrar' : 'Enviar'
  // Boletos da seleção em cooldown — alerta no modal, mas o disparo pode prosseguir
  // (backend pula esses com erro 'cooldown' explícito).
  const selectionInCooldown = selectedBoletos.filter(b => isCooldownActive(b)).length

  return (
    // Preenche o box global do App (que já dá respiro + cartão + sombra + scroll).
    // Flex-row: coluna principal + dossiê lateral.
    <div className="flex h-full overflow-hidden neu-forms">
    {/* PLANOS (21/10/2026): cabeçalho da tela + cartões de número + uma faixa
        com filtros e ações + a lista em bloco. Nada solto no chão. */}
    <div className="flex-1 flex flex-col min-w-0 overflow-hidden p-4 sm:p-6 pln-tela-fin">
      {/* ── Cabeçalho: título + ações ── */}
      <div className="pln-cab">
        <div className="pln-cab-icone"><LuBanknote /></div>
        <div className="min-w-0">
          <h1 className="pln-cab-titulo">Cobrança</h1>
          <div className="pln-cab-sub">Boletos em atraso, cobrança pelo WhatsApp e reenvio</div>
        </div>

        <div className="pln-cab-acoes">
          <button
            onClick={() => setChartsExpanded(v => !v)}
            className={`neu-tab flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${chartsExpanded ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-secondary hover:text-foreground'}`}
            title="Análises (gráficos)"
          >
            <FiBarChart2 className="w-3.5 h-3.5" />
            Análises
            {chartsExpanded ? <FiChevronUp className="w-3 h-3" /> : <FiChevronDown className="w-3 h-3" />}
          </button>
          <button
            onClick={loadDashboard}
            disabled={isLoading}
            className="neu-tab flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors disabled:opacity-50"
            title="Recarregar do Oracle"
          >
            <FiRefreshCw className={`w-3.5 h-3.5 ${isLoading ? 'animate-spin' : ''}`} />
            Atualizar
          </button>
          {data?.timestamp && (
            <span className="text-xs text-muted-foreground hidden md:inline" title="Hora da última leitura do Oracle">
              {new Date(data.timestamp).toLocaleTimeString('pt-BR')}
            </span>
          )}
        </div>
      </div>

      {/* ── Números da carteira em atraso ── */}
      {data && (
        <PlnKpis>
          <PlnKpi rotulo="Em atraso" tom="ruim" valor={formatCurrency(data.summary.totalSaldo)} pe="saldo dos boletos vencidos" />
          <PlnKpi rotulo="Boletos" valor={data.summary.totalBoletos.toLocaleString('pt-BR')} pe="em aberto após o vencimento" />
          <PlnKpi rotulo="Clientes" valor={data.summary.totalClientes.toLocaleString('pt-BR')} pe="com pelo menos um boleto" />
          <PlnKpi rotulo="Ticket médio" valor={formatCurrency(data.summary.ticketMedio)} pe="por cliente" />
        </PlnKpis>
      )}

      {/* ── Análises — visão EXCLUSIVA de métricas: enquanto aberta, a parte
          operacional (filtros, seleção, tabela de boletos com reenvio) fica
          oculta. Análise é leitura; disparo é na visão normal. ── */}
      {chartsExpanded && data && (
        <div className="flex-1 min-h-0 overflow-y-auto">
          {/* Preenche a altura disponível: KPIs fixos em cima, corpo elástico
              embaixo (Recuperados alto à esquerda + gráficos empilhados à
              direita). Em tela baixa, o min-h garante scroll em vez de esmagar.
              PLANOS (21/10/2026): a efetividade virou faixa (título + janela) +
              cartões PlnKpi; cada lista/gráfico virou PlnBloco com título 16. */}
          <div className="h-full min-h-[560px] flex flex-col pln-corpo-tela">
            {/* Efetividade da cobrança: cruzamento disparos WhatsApp × DOCCLIENTES */}
            <PlnBarra>
              <span className="text-[15px] font-semibold text-foreground">Efetividade da cobrança</span>
              <span className="text-[12.5px] text-muted-foreground">disparos WhatsApp × pagamentos DRACO</span>
              <PlnBarraSep />
              <PlnBarraRot>Janela</PlnBarraRot>
              <div className="flex items-center gap-1">
                {efetividadeJanelas.map(j => (
                  <button
                    key={j.key}
                    onClick={() => setEfetividadeJanela(j.key)}
                    className={`h-8 px-3 rounded-lg text-[13px] font-medium transition-colors ${efetividadeJanela === j.key ? 'bg-primary text-primary-foreground' : 'bg-secondary/50 text-muted-foreground hover:bg-secondary'}`}
                    title={j.key === 3 ? 'D+3 — janela curta de baixa bancária' : j.key === 7 ? 'D+7 — janela padrão de baixa do DRACO' : `Últimos ${j.key} dias`}
                  >
                    {j.label}
                  </button>
                ))}
              </div>
              {efetividadeLoading && (
                <span className="ml-auto flex items-center gap-2 text-[13px] text-muted-foreground">
                  <FiLoader className="w-3.5 h-3.5 animate-spin" /> Consultando Oracle…
                </span>
              )}
              {efetividadeError && (
                <span className="ml-auto text-[13px] text-destructive">{efetividadeError}</span>
              )}
              {!efetividadeLoading && !efetividadeError && efetividade && efetividade.disparos === 0 && (
                <span className="ml-auto text-[13px] text-muted-foreground">Nenhum disparo nessa janela.</span>
              )}
            </PlnBarra>
            {!efetividadeLoading && !efetividadeError && efetividade && efetividade.disparos > 0 && (
              <PlnKpis>
                <PlnKpi tom="ok" cor="#10b981"
                  rotulo={<span title="Boletos cobrados na janela com pagamento APÓS a cobrança">Conversão</span>}
                  valor={`${efetividade.kpis.conversao}%`}
                  pe={`${efetividade.kpis.pagoApos} de ${efetividade.disparos}`} />
                <PlnKpi cor="#3b82f6"
                  rotulo={<span title="Soma de DCVALORPAGO dos boletos pagos após cobrança (inclui juros/multa)">Recuperado</span>}
                  valor={formatCurrency(efetividade.kpis.valorRecuperado)}
                  pe={`em ${efetividade.kpis.pagoApos} boletos`} />
                <PlnKpi cor="#8b5cf6"
                  rotulo={<span title="Mediana de dias entre a cobrança e o pagamento">Tempo até pagar</span>}
                  valor={efetividade.kpis.delayMediano !== null ? `${efetividade.kpis.delayMediano}d` : '—'}
                  pe={`médio ${efetividade.kpis.delayMedio ?? '—'}d`} />
                <PlnKpi cor="#f59e0b"
                  rotulo={<span title="Cobrados com pagamento anterior a QUALQUER cobrança conhecida (lista snapshot atrasada)">Desperdício</span>}
                  valor={`${efetividade.kpis.desperdicio}%`}
                  pe={`${efetividade.kpis.pagoAntes} já pagos`} />
                <PlnKpi tom="ruim" cor="#ef4444"
                  rotulo={<span title="Cobrados sem pagamento registrado em DOCCLIENTES">Inadimplentes</span>}
                  valor={efetividade.kpis.semPagto}
                  pe={efetividade.kpis.parcial > 0 ? `· ${efetividade.kpis.parcial} parciais` : 'sem retorno'} />
              </PlnKpis>
            )}

            {/* Corpo elástico: Recuperados (2/3) + gráficos empilhados (1/3) */}
            <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Coluna esquerda: Recuperados + Intenções de pagamento */}
              <div className="lg:col-span-2 flex flex-col gap-4 min-h-0">
              {/* Recuperados: boletos pagos APÓS a cobrança, nominais (substituiu
                  o gráfico "Distribuição do tempo de resposta", em desuso) */}
              <PlnBloco
                titulo="Recuperados após cobrança"
                nota={efetividade ? (
                  <>
                    {efetividade.kpis.pagoApos} boleto{efetividade.kpis.pagoApos === 1 ? '' : 's'} · {formatCurrency(efetividade.kpis.valorRecuperado)}
                    {(efetividade.recuperados?.length || 0) > 0 && (efetividade.recuperados?.length || 0) < efetividade.kpis.pagoApos && (
                      <> · mostrando os {efetividade.recuperados!.length} pagamentos mais recentes de {efetividade.kpis.pagoApos}</>
                    )}
                  </>
                ) : undefined}
                className="flex flex-col flex-1 min-h-[240px]"
              >
                {(efetividadeLoading || !efetividade) ? (
                  <div className="flex items-center gap-2 text-[13px] text-muted-foreground px-5 py-4">
                    <FiLoader className="w-3.5 h-3.5 animate-spin" /> Consultando Oracle…
                  </div>
                ) : (efetividade.recuperados?.length || 0) === 0 ? (
                  <div className="text-[13px] text-muted-foreground px-5 py-4">Nenhum pagamento após cobrança nessa janela.</div>
                ) : (
                    <div className="flex-1 min-h-0 overflow-y-auto scrollbar-thin">
                      <table className="w-full text-[13px]">
                        <thead className="bg-card sticky top-0 shadow-[0_1px_0_hsl(var(--border))]">
                          <tr className="text-[12.5px]">
                            <th className="text-left pl-5 pr-3 py-2 text-muted-foreground font-medium">Cliente</th>
                            <th className="text-left px-3 py-2 text-muted-foreground font-medium">Doc</th>
                            <th className="text-left px-3 py-2 text-muted-foreground font-medium">Cobrado em</th>
                            <th className="text-left px-3 py-2 text-muted-foreground font-medium">Pago em</th>
                            <th className="text-right px-3 py-2 text-muted-foreground font-medium" title="Dias entre a cobrança e o pagamento">Dias</th>
                            <th className="text-right px-3 py-2 text-muted-foreground font-medium">Valor pago</th>
                            <th className="text-center pl-3 pr-5 py-2 text-muted-foreground font-medium">Situação</th>
                          </tr>
                        </thead>
                        <tbody>
                          {efetividade.recuperados!.map(r => (
                            <tr key={`${r.documento}-${r.pagoEm}`} className="neu-tr border-t border-border/40 hover:bg-secondary/30">
                              <td className="pl-5 pr-3 py-2">
                                <span className="text-foreground">{r.nome || '—'}</span>
                                {r.cliente != null && <span className="text-muted-foreground/70 ml-1">· {r.cliente}</span>}
                              </td>
                              <td className="px-3 py-2 font-mono text-muted-foreground">{r.documento}</td>
                              <td className="px-3 py-2 text-muted-foreground">{formatDate(r.disparoEm.slice(0, 10))}</td>
                              <td className="px-3 py-2 text-muted-foreground">{formatDate(r.pagoEm)}</td>
                              <td className="px-3 py-2 text-right tabular-nums">{r.dias}d</td>
                              <td className="px-3 py-2 text-right tabular-nums font-medium text-foreground">{formatCurrency(r.valorPago)}</td>
                              <td className="pl-3 pr-5 py-2 text-center">
                                {r.parcial ? (
                                  <span title={`Pagamento parcial — ainda restam ${formatCurrency(r.saldoRestante)}`}
                                    className="neu-tag inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-500/10 text-amber-600">
                                    <FiCheck className="w-3 h-3" /> Parcial
                                  </span>
                                ) : (
                                  <span title="Boleto quitado após a cobrança"
                                    className="neu-relief inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-500/10 text-emerald-600">
                                    <FiCheck className="w-3 h-3" /> Recuperado
                                  </span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                )}
              </PlnBloco>

              {/* Intenção de pagamento: respondeu após a cobrança mas AINDA sem
                  baixa no DRACO (ex.: mandou comprovante). Quando a baixa cai,
                  o item migra pra Recuperados e some daqui sozinho. */}
              {efetividade && (efetividade.intencoes?.length || 0) > 0 && (
                <PlnBloco
                  titulo="Intenção de pagamento"
                  nota={<>{efetividade.intencoes!.length} resposta{efetividade.intencoes!.length === 1 ? '' : 's'} após cobrança · aguardando baixa no DRACO — sai da lista quando o pagamento baixar</>}
                  className="flex flex-col flex-shrink-0 max-h-[40%]"
                >
                  <div className="flex-1 min-h-0 overflow-y-auto scrollbar-thin">
                    <table className="w-full text-[13px]">
                      <thead className="bg-card sticky top-0 shadow-[0_1px_0_hsl(var(--border))]">
                        <tr className="text-[12.5px]">
                          <th className="text-left pl-5 pr-3 py-2 text-muted-foreground font-medium">Cliente</th>
                          <th className="text-left px-3 py-2 text-muted-foreground font-medium">Doc</th>
                          <th className="text-right px-3 py-2 text-muted-foreground font-medium" title="Saldo do boleto no momento da cobrança">Cobrado</th>
                          <th className="text-left px-3 py-2 text-muted-foreground font-medium">Respondeu em</th>
                          <th className="text-center px-3 py-2 text-muted-foreground font-medium">Sinal</th>
                          <th className="pl-3 pr-5 py-2"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {efetividade.intencoes!.map(i => (
                          <tr key={`${i.documento}-${i.respostaEm}`} className="neu-tr border-t border-border/40 hover:bg-secondary/30"
                            title={i.resumo ? `Última resposta: ${i.resumo}` : undefined}>
                            <td className="pl-5 pr-3 py-2 whitespace-nowrap">
                              <span className="text-foreground">{i.nome || '—'}</span>
                              {i.cliente != null && <span className="text-muted-foreground/70 ml-1">· {i.cliente}</span>}
                            </td>
                            <td className="px-3 py-2 font-mono text-muted-foreground">{i.documento}</td>
                            <td className="px-3 py-2 text-right tabular-nums">
                              {formatCurrency(i.valorCobrado)}
                              {i.valorMencionado != null && (
                                <span className="block text-xs text-muted-foreground" title="Valor citado pelo cliente na conversa">citou {formatCurrency(i.valorMencionado)}</span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">{formatDate(i.respostaEm.slice(0, 10))}</td>
                            <td className="px-3 py-2 text-center whitespace-nowrap">
                              {i.tipo === 'comprovante' ? (
                                <span title="Enviou imagem/documento após a cobrança — provável comprovante"
                                  className="neu-tag inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-violet-500/10 text-violet-600">
                                  <FiFileText className="w-3 h-3" /> Comprovante
                                </span>
                              ) : i.tipo === 'quer_pagar' ? (
                                <span title="Pediu Pix/boleto ou prometeu pagar — quer pagar, aguardando"
                                  className="neu-tag inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-500/10 text-amber-600">
                                  <FiClock className="w-3 h-3" /> Quer pagar
                                </span>
                              ) : i.tipo === 'informou_pagamento' ? (
                                <span title="Mencionou pagamento/PIX/transferência na resposta"
                                  className="neu-tag inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-500/10 text-blue-600">
                                  <FiCheck className="w-3 h-3" /> Disse que pagou
                                </span>
                              ) : (
                                <span title="Respondeu após a cobrança (conteúdo sem sinal claro de pagamento)"
                                  className="neu-tag inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-secondary text-muted-foreground">
                                  Respondeu
                                </span>
                              )}
                            </td>
                            <td className="pl-3 pr-5 py-2 text-right">
                              <button
                                onClick={() => i.conversationId && onOpenConversation?.(i.conversationId)}
                                disabled={!i.conversationId || !onOpenConversation}
                                title="Abrir a conversa WhatsApp deste cliente no painel de Conversas"
                                className="neu-relief inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500/20 disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
                              >
                                <FiMessageCircle className="w-3 h-3" /> Ver conversa
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </PlnBloco>
              )}
              </div>

              {/* Gráficos empilhados na coluna direita */}
              <div className="flex flex-col gap-4 min-h-0">
                <PlnBloco titulo="Por Faixa de Atraso" className="flex-1 min-h-[180px] flex flex-col">
                  <div className="flex-1 min-h-0 px-3 pt-3 pb-2">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={data.porFaixa}><CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" /><XAxis dataKey="faixa" tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }} /><YAxis tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }} /><Tooltip content={<CustomTooltip />} /><Bar dataKey="quantidade" name="quantidade" fill={MONEY.overdue} radius={[3, 3, 0, 0]} /></BarChart>
                    </ResponsiveContainer>
                  </div>
                </PlnBloco>
                <PlnBloco
                  titulo="Por Grupo (valor)"
                  nota="top 7"
                  extra={porGrupoBars.outros > 0 ? (
                    <span className="text-[12.5px] text-muted-foreground" title="Soma dos demais grupos, fora do ranking">
                      Outros: {formatCurrency(porGrupoBars.outros)}
                    </span>
                  ) : undefined}
                  className="flex-1 min-h-[180px] flex flex-col"
                >
                  {/* Ranking em barras horizontais (a pizza era ilegível com N
                      fatias pequenas): um matiz só — magnitude, não identidade. */}
                  <div className="flex-1 min-h-0 px-3 pt-3 pb-2">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart layout="vertical" data={porGrupoBars.top} margin={{ left: 0, right: 40, top: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
                        <XAxis type="number" tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                          tickFormatter={(v: number) => v >= 1000 ? `${Math.round(v / 1000)}k` : String(v)} />
                        <YAxis type="category" dataKey="grupo" width={96} interval={0}
                          tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                          tickFormatter={(g: string) => String(g).length > 12 ? `${String(g).slice(0, 11)}…` : String(g)} />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="valor" name="valor" fill={MONEY.balance} radius={[0, 3, 3, 0]} maxBarSize={16}>
                          <LabelList dataKey="valor" position="right"
                            formatter={(v: number) => v >= 1000 ? `${Math.round(v / 1000)}k` : String(Math.round(v))}
                            style={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }} />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </PlnBloco>
                <PlnBloco titulo="Por Semana" className="flex-1 min-h-[180px] flex flex-col">
                  <div className="flex-1 min-h-0 px-3 pt-3 pb-2">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={data.porSemana}><CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" /><XAxis dataKey="semana" tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }} /><YAxis tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }} /><Tooltip content={<CustomTooltip />} /><Area type="monotone" dataKey="valor" name="valor" stroke={MONEY.out} fill={MONEY.out} fillOpacity={0.15} /></AreaChart>
                    </ResponsiveContainer>
                  </div>
                </PlnBloco>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Filtros (linha 1) + seleção e disparo (linha 2) — uma faixa de
          papel só, na visão operacional ── */}
      {!chartsExpanded && (
      <div className="pln-barra pln-barra-dupla shrink-0">
      <div className="pln-barra-linha">
        <span className="pln-barra-rot">Faixa</span>
        {FAIXA_OPTIONS.map(opt => (
          <FilterChip key={opt.key} active={filterFaixa.has(opt.key)} onClick={() => toggleFaixa(opt.key)}>{opt.label}</FilterChip>
        ))}
        <span className="pln-barra-sep" />
        <span className="pln-barra-rot">Status</span>
        {STATUS_OPTIONS.map(opt => (
          <FilterChip key={opt.key} active={filterStatus.has(opt.key)} onClick={() => toggleStatus(opt.key)}>{opt.label}</FilterChip>
        ))}
        <span className="pln-barra-sep" />
        <select
          value={filterGrupo || ''}
          onChange={e => setFilterGrupo(e.target.value || null)}
          className="neu-convex h-8 px-2 text-sm bg-secondary/50 border border-border rounded-md focus:outline-none focus:ring-1 focus:ring-primary [color-scheme:light] dark:[color-scheme:dark]"
          title="Filtrar por grupo"
        >
          <option value="">Todos os grupos</option>
          {uniqueGrupos.map(g => <option key={g} value={g}>{g}</option>)}
        </select>
        <div className="relative ml-auto">
          <FiSearch className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
          <input
            type="text"
            placeholder="Buscar nome, código, doc…"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="h-8 pl-7 pr-2 text-sm bg-secondary/50 border border-border rounded-md w-64 focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>
        {hasActiveFilters && (
          <button onClick={clearFilters} className="text-[10px] text-muted-foreground hover:text-foreground underline ml-1" title="Limpar todos os filtros">
            limpar
          </button>
        )}
      </div>

      {/* ── seleção + CTA ── */}
      <div className="pln-barra-linha pln-barra-fio">
        <button
          onClick={selectPending} disabled={sending || !!resendingDoc}
          className="neu-relief flex items-center gap-1.5 px-2.5 py-1 bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 text-amber-600 rounded-md text-[11px] font-medium transition-colors disabled:opacity-50"
          title="Selecionar apenas boletos ainda não cobrados (do filtro atual)"
        >
          <FiCheckSquare className="w-3 h-3" />
          Selecionar pendentes
        </button>
        <button
          onClick={selectCobrados} disabled={sending || !!resendingDoc}
          className="neu-relief flex items-center gap-1.5 px-2.5 py-1 bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 text-emerald-600 rounded-md text-[11px] font-medium transition-colors disabled:opacity-50"
          title="Selecionar boletos já cobrados (fora do cooldown) — pra reenviar"
        >
          <FiRefreshCw className="w-3 h-3" />
          Selecionar reenvio
        </button>
        {selected.size > 0 && (
          <span className="text-[10px] text-muted-foreground ml-1">
            {selectedClientes} cliente{selectedClientes === 1 ? '' : 's'} · {formatCurrency(selectedTotal)}
          </span>
        )}
        <div className="ml-auto flex items-center gap-2">
          <span className="text-[10px] text-muted-foreground hidden lg:inline">Template: <span className="font-mono">{cobrancaTemplate}</span></span>
          <button
            onClick={handleSend}
            disabled={sending || selected.size === 0 || !!resendingDoc || selectedClientes > COBRANCA_BATCH_CAP}
            title={selectedClientes > COBRANCA_BATCH_CAP
              ? `Limite por batch: ${COBRANCA_BATCH_CAP} clientes. Reduza a seleção e dispare em mais de um lote.`
              : ''}
            className="neu-relief flex items-center gap-1.5 px-3 py-1 bg-emerald-500 hover:bg-emerald-600 text-white rounded-md text-[11px] font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {sending ? <FiLoader className="w-3 h-3 animate-spin" /> : (selectionAllCobrados ? <FiRefreshCw className="w-3 h-3" /> : <FiSend className="w-3 h-3" />)}
            {sending
              ? (loadingPhones ? 'Buscando tel...' : 'Enviando...')
              : selectedClientes > COBRANCA_BATCH_CAP
                ? `Limite ${COBRANCA_BATCH_CAP} — reduza ${selectedClientes - COBRANCA_BATCH_CAP}`
                : `${sendLabel} ${selectedClientes} cliente${selectedClientes === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>
      </div>
      )}

      {/* ── Send results (sticky, collapsible) ── */}
      {!chartsExpanded && sendProgress && sendResults && (
        <div className={`flex-shrink-0 rounded-xl border border-border text-[11px] ${sendProgress.errors > 0 ? 'bg-amber-500/10' : 'bg-emerald-500/10'}`}>
          <div className="flex items-center gap-2 px-3 py-1.5">
            <FiCheck className="w-3.5 h-3.5 text-emerald-500" />
            <span className="font-medium">{sendProgress.sent} enviados</span>
            {sendProgress.errors > 0 && <span className="text-red-500">· {sendProgress.errors} erros</span>}
            <span className="text-muted-foreground">de {sendProgress.total}</span>
            <button onClick={() => setResultsExpanded(v => !v)} className="ml-auto text-muted-foreground hover:text-foreground" title="Detalhes">
              {resultsExpanded ? <FiChevronUp className="w-3 h-3" /> : <FiChevronDown className="w-3 h-3" />}
            </button>
            <button onClick={() => { setSendResults(null); setSendProgress(null) }} className="text-muted-foreground hover:text-foreground" title="Dispensar">
              <FiX className="w-3 h-3" />
            </button>
          </div>
          {resultsExpanded && sendResults.filter(r => r.status === 'error').length > 0 && (
            <div className="px-3 pb-1.5 max-h-24 overflow-y-auto">
              {sendResults.filter(r => r.status === 'error').map((r, i) => (
                <div key={i} className="text-[10px] text-muted-foreground">
                  <span className="text-red-500 font-medium">{r.cliente}</span>: {r.error}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Erros gerais ── */}
      {error && (
        <div className="neu-relief flex-shrink-0 flex items-center gap-2 px-4 py-2 rounded-xl bg-destructive/10 border border-destructive/30 text-sm">
          <FiAlertCircle className="w-4 h-4 text-destructive flex-shrink-0" />
          <span className="text-destructive flex-1">{error}</span>
          <button onClick={() => setError(null)}><FiX className="w-3.5 h-3.5" /></button>
        </div>
      )}

      {/* ── Loading state quando ainda não tem data ── */}
      {isLoading && !data && (
        <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground">
          <FiRefreshCw className="w-8 h-8 animate-spin mb-3" />
          <span className="text-sm">Consultando Oracle...</span>
        </div>
      )}

      {/* ── Tabela única (com checkboxes sempre) — só na visão operacional ── */}
      {!chartsExpanded && data && (
        <div className="flex-1 min-h-0 overflow-hidden">
          {renderTable()}
        </div>
      )}

      {/* ── Modal de confirmação — bloqueia 1-clique acidental ── */}
      {confirmAction && (() => {
        const isBatch = confirmAction.type === 'batch'
        const boleto = !isBatch ? confirmAction.boleto : null
        const reenvioBatch = isBatch && selectionAllCobrados
        const title = isBatch
          ? (reenvioBatch
              ? `Reenviar cobrança pra ${selectedClientes} cliente${selectedClientes === 1 ? '' : 's'}?`
              : `Disparar cobrança pra ${selectedClientes} cliente${selectedClientes === 1 ? '' : 's'}?`)
          : `Reenviar cobrança pra ${boleto!.nome}?`
        const details = isBatch
          ? [
              `${selectedBoletos.length} boleto${selectedBoletos.length === 1 ? '' : 's'} selecionado${selectedBoletos.length === 1 ? '' : 's'}`,
              `Valor total: ${formatCurrency(selectedTotal)}`,
              `Template: ${cobrancaTemplate}`,
              ...(selectionInCooldown > 0
                ? [`⚠ ${selectionInCooldown} em cooldown — serão puladas pelo servidor`]
                : []),
            ]
          : [
              `Cliente ${boleto!.cliente} · Doc ${boleto!.documento}`,
              `Valor: ${formatCurrency(boleto!.saldo)} · Vencimento ${formatDate(boleto!.vencimento)}`,
              ...(boleto!.cobradoEm
                ? [`Última cobrança: ${formatRelativeDays(boleto!.cobradoEm)}${(boleto!.cobrancasCount || 0) > 1 ? ` · ${boleto!.cobrancasCount}x enviadas` : ''}`]
                : []),
            ]
        return (
          <ConfirmDialog
            isOpen={true}
            type="warning"
            title={title}
            message={isBatch
              ? 'Cada cliente receberá uma mensagem template via WhatsApp Meta. A ação não pode ser desfeita.'
              : 'O cliente receberá uma mensagem template via WhatsApp Meta. A ação não pode ser desfeita.'}
            details={details}
            confirmText={reenvioBatch || !isBatch ? 'Reenviar' : 'Disparar'}
            cancelText="Cancelar"
            onCancel={() => { if (!confirmingRef.current) setConfirmAction(null) }}
            onConfirm={() => {
              if (confirmingRef.current) return // dedup clique duplo (state é async, ref é sync)
              confirmingRef.current = true
              const action = confirmAction
              setConfirmAction(null)
              const run = async () => {
                try {
                  if (action.type === 'batch') await handleSendConfirmed()
                  else await handleResendConfirmed(action.boleto)
                } finally {
                  confirmingRef.current = false
                }
              }
              run()
            }}
          />
        )
      })()}
    </div>

    {/* ── Right pane: dossier do cliente clicado ── */}
    {dossierContext && (
      <DossierPane
        loading={dossierLoading}
        error={dossierError}
        data={dossierData}
        onOpenConversation={onOpenConversation}
        activeDoc={dossierContext.activeDoc}
        boletosAbertos={dossierBoletos}
        totalAberto={dossierTotalAberto}
        onClose={closeDossier}
        onReload={() => setDossierContext({ ...dossierContext })}
        onResendBoleto={handleResend}
        isCooldownActive={isCooldownActive}
        cooldownRemainingLabel={cooldownRemainingLabel}
        formatRelativeDays={formatRelativeDays}
        resendingDoc={resendingDoc}
      />
    )}
    </div>
  )

  function renderTable() {
    // Linha de um boleto — reusada na lista flat e como filho de cada grupo.
    const renderBoletoRow = (b: Boleto, opts?: { child?: boolean }) => {
      const key = bKey(b), isSel = selected.has(key)
      const bg = b.dias_atraso <= 7 ? 'bg-amber-500/10 text-amber-600' : b.dias_atraso <= 30 ? 'bg-orange-500/10 text-orange-600' : 'bg-red-500/10 text-red-600'
      const sent = sendResults?.find(r => r.cliente === b.cliente)
      const isInDossier = dossierContext?.codigo === b.cliente
      const rowBg = isInDossier ? 'bg-primary/10' : isSel ? 'bg-primary/5' : opts?.child ? 'bg-muted/5' : ''
      return (
        <tr key={key}
            className={`hover:bg-secondary/30 transition-colors cursor-pointer ${rowBg}`}
            onClick={() => openDossier(b)}>
          <td className="px-2 py-1.5" onClick={(e) => { e.stopPropagation(); toggleSelect(b) }}>
            <NeuCheck state={isSel ? 'on' : 'off'} />
          </td>
          <td className={`px-2 py-1.5 font-mono text-muted-foreground ${opts?.child ? 'pl-6' : ''}`}>
            {opts?.child && <span className="text-muted-foreground/40">↳ </span>}{b.cliente}
          </td>
          <td className="px-2 py-1.5 font-medium text-foreground max-w-[180px] truncate">
            {opts?.child ? <span className="text-muted-foreground/40">—</span> : b.nome}
          </td>
          <td className="px-2 py-1.5 text-muted-foreground max-w-[120px] truncate">{opts?.child ? '' : b.grupo}</td>
          <td className="px-2 py-1.5 font-mono text-muted-foreground">{b.documento}</td>
          <td className="px-2 py-1.5 text-muted-foreground">{formatDate(b.vencimento)}</td>
          <td className="px-2 py-1.5 text-right"><span className={`inline-block px-1 py-0.5 rounded text-[9px] font-bold ${bg}`}>{b.dias_atraso}d</span></td>
          <td className="px-2 py-1.5 text-right font-semibold text-red-500">{formatCurrency(b.saldo)}</td>
          {/* Resp.: badge azul se cliente respondeu após última cobrança */}
          <td className="px-2 py-1.5 text-center">
            {b.respondeu ? (
              <span
                className="neu-tag inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-medium bg-sky-500/15 text-sky-500"
                title={`Respondeu ${b.respostaEm ? formatRelativeDays(b.respostaEm) : 'após cobrança'}`}
              >
                <FiMessageCircle className="w-2.5 h-2.5" />
                {b.respostaEm ? formatRelativeDays(b.respostaEm).replace('há ', '') : 'sim'}
              </span>
            ) : (
              <span className="text-muted-foreground/30">—</span>
            )}
          </td>
          <td className="px-2 py-1.5 text-center">
            {(() => {
              const isResending = resendingDoc === b.documento
              if (sent?.status === 'error') {
                return <span className="neu-tag px-1.5 py-0.5 rounded-full text-[9px] font-medium bg-red-500/10 text-red-600" title={sent.error}>Erro</span>
              }
              if (b.cobrado || sent?.status === 'sent') {
                const cooldownActive = isCooldownActive(b)
                const tipParts: string[] = []
                if (b.cobradoEm) tipParts.push(`última: ${formatRelativeDays(b.cobradoEm)}`)
                if ((b.cobrancasCount || 0) > 1) tipParts.push(`${b.cobrancasCount}x enviadas`)
                if (cooldownActive) tipParts.push(`aguarde ${cooldownRemainingLabel(b)}`)
                const tip = cooldownActive
                  ? `Cooldown ativo — ${tipParts.join(' · ')}`
                  : (tipParts.length ? `Reenviar — ${tipParts.join(' · ')}` : 'Reenviar cobrança')
                const disabled = isResending || !!resendingDoc || sending || cooldownActive
                const inlineInfo: string[] = []
                const inlineTitleParts: string[] = []
                if (b.cobradoEm) {
                  inlineInfo.push(formatRelativeDays(b.cobradoEm).replace('há ', ''))
                  inlineTitleParts.push(`Última cobrança: ${formatRelativeDays(b.cobradoEm)}`)
                }
                if ((b.cobrancasCount || 0) > 1) {
                  inlineInfo.push(`${b.cobrancasCount}x`)
                  inlineTitleParts.push(`${b.cobrancasCount}x cobranças enviadas no total`)
                }
                return (
                  <div className="inline-flex items-center gap-1">
                    {inlineInfo.length > 0 && (
                      <span
                        className="text-[9px] text-muted-foreground/80 whitespace-nowrap"
                        title={inlineTitleParts.join(' · ')}
                      >
                        {inlineInfo.join(' · ')}
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={e => { e.stopPropagation(); handleResend(b) }}
                      disabled={disabled}
                      title={tip}
                      aria-label={cooldownActive ? `Cooldown ativo, aguarde ${cooldownRemainingLabel(b)}` : 'Reenviar cobrança'}
                      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-medium transition-colors disabled:cursor-not-allowed ${
                        cooldownActive
                          ? 'bg-muted text-muted-foreground/70 disabled:opacity-100'
                          : 'bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500/20 hover:text-emerald-700 disabled:opacity-50'
                      }`}
                    >
                      {isResending
                        ? <FiLoader className="w-3 h-3 animate-spin" />
                        : (cooldownActive ? <FiClock className="w-3 h-3" /> : <FiRefreshCw className="w-3 h-3" />)}
                      {isResending ? 'Reenviando' : (cooldownActive ? cooldownRemainingLabel(b) : 'Reenviar')}
                    </button>
                  </div>
                )
              }
              return <span className="text-muted-foreground/30">—</span>
            })()}
          </td>
        </tr>
      )
    }

    return (
      <div className="glass-panel rounded-xl overflow-hidden flex flex-col h-full">
        <div className="neu-bar pln-bloco-cab flex items-center justify-between border-b border-border flex-shrink-0 glass-bar">
          <h3 className="pln-bloco-titulo">
            Boletos em atraso
            <span className="pln-bloco-nota ml-2 font-normal">
              {filteredBoletos.length === data!.boletos.length
                ? `${data!.boletos.length}`
                : `${filteredBoletos.length} de ${data!.boletos.length}`}
            </span>
          </h3>
          <div className="flex items-center gap-1.5">
            {viewMode === 'grouped' && multiGroups.length > 0 && (
              <button
                onClick={() => (allExpanded ? collapseAll() : expandAll())}
                className="neu-tab text-[10px] px-1.5 py-0.5 rounded text-muted-foreground hover:text-foreground hover:bg-secondary/50 transition-colors"
                title="Expandir/recolher os clientes com várias cobranças"
              >
                {allExpanded ? 'Recolher todos' : 'Expandir todos'}
              </button>
            )}
            <div className="inline-flex items-center rounded-lg border border-border/60 overflow-hidden text-[10px] glass-bar">
              <button
                onClick={() => setViewMode('grouped')}
                className={`px-2 py-0.5 transition-colors ${viewMode === 'grouped' ? 'bg-primary/15 text-primary font-medium' : 'text-muted-foreground hover:bg-secondary/50'}`}
                title="Agrupar por cliente"
              >Agrupado</button>
              <button
                onClick={() => setViewMode('flat')}
                className={`px-2 py-0.5 transition-colors ${viewMode === 'flat' ? 'bg-primary/15 text-primary font-medium' : 'text-muted-foreground hover:bg-secondary/50'}`}
                title="Lista corrida (flat)"
              >Lista</button>
            </div>
          </div>
        </div>
        <div className="flex-1 overflow-auto px-5">
          <table className="w-full text-[11px]">
            <thead className="bg-card sticky top-0 z-10 shadow-[0_1px_0_hsl(var(--border))]">
              <tr>
                <th className="px-2 py-1.5 w-8">
                  <button onClick={selectAll} className="text-muted-foreground hover:text-foreground">
                    <NeuCheck state={selected.size === filteredBoletos.length && filteredBoletos.length > 0 ? 'on' : 'off'} />
                  </button>
                </th>
                <th className="text-left px-2 py-1.5 text-muted-foreground font-medium">Código</th>
                <th className="text-left px-2 py-1.5 text-muted-foreground font-medium cursor-pointer select-none" onClick={() => handleSort('nome')}>
                  <span className="flex items-center gap-0.5">Nome <SortIcon field="nome" /></span>
                </th>
                <th className="text-left px-2 py-1.5 text-muted-foreground font-medium">Grupo</th>
                <th className="text-left px-2 py-1.5 text-muted-foreground font-medium">Doc</th>
                <th className="text-left px-2 py-1.5 text-muted-foreground font-medium">Venc.</th>
                <th className="text-right px-2 py-1.5 text-muted-foreground font-medium cursor-pointer select-none" onClick={() => handleSort('dias_atraso')}>
                  <span className="flex items-center justify-end gap-0.5">Atraso <SortIcon field="dias_atraso" /></span>
                </th>
                <th className="text-right px-2 py-1.5 text-muted-foreground font-medium cursor-pointer select-none" onClick={() => handleSort('saldo')}>
                  <span className="flex items-center justify-end gap-0.5">Saldo <SortIcon field="saldo" /></span>
                </th>
                <th className="text-center px-2 py-1.5 text-muted-foreground font-medium" title="Cliente respondeu após a última cobrança">Resp.</th>
                <th className="text-center px-2 py-1.5 text-muted-foreground font-medium">Cobrança</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/30">
              {viewMode === 'flat'
                ? filteredBoletos.map(b => renderBoletoRow(b))
                : groupedBoletos.map(g => {
                    // 1 boleto (95% dos casos) = linha normal, sem mãe — agrupar e
                    // recolher esconderia doc/Reenviar à toa. Só 2+ vira grupo.
                    if (g.count === 1) return renderBoletoRow(g.boletos[0])
                    const expanded = !collapsedClients.has(g.cliente)
                    const keys = g.boletos.map(bKey)
                    const selCount = keys.filter(k => selected.has(k)).length
                    const checkState = selCount === 0 ? 'none' : selCount === keys.length ? 'all' : 'some'
                    const atrasoBg = g.maxAtraso <= 7 ? 'bg-amber-500/10 text-amber-600' : g.maxAtraso <= 30 ? 'bg-orange-500/10 text-orange-600' : 'bg-red-500/10 text-red-600'
                    const isInDossier = dossierContext?.codigo === g.cliente
                    return (
                      <Fragment key={g.cliente}>
                        <tr
                          className={`cursor-pointer transition-colors ${isInDossier ? 'bg-primary/10' : checkState !== 'none' ? 'bg-primary/5' : 'hover:bg-secondary/30'}`}
                          onClick={() => toggleExpanded(g.cliente)}>
                          <td className="px-2 py-1.5" onClick={(e) => { e.stopPropagation(); toggleGroupSelect(g.boletos) }}>
                            <NeuCheck state={checkState === 'all' ? 'on' : checkState === 'some' ? 'some' : 'off'} />
                          </td>
                          <td className="px-2 py-1.5 font-mono text-muted-foreground">
                            <span className="inline-flex items-center gap-1">
                              {expanded ? <FiChevronDown className="w-3.5 h-3.5" /> : <FiChevronRight className="w-3.5 h-3.5" />}
                              <span
                                className="hover:text-primary transition-colors"
                                onClick={(e) => { e.stopPropagation(); openDossier(g.boletos[0]) }}
                                title="Ver dossier do cliente"
                              >{g.cliente}</span>
                            </span>
                          </td>
                          <td className="px-2 py-1.5 font-semibold text-foreground max-w-[180px] truncate">
                            {g.nome}
                            <span className="ml-1.5 text-[9px] text-muted-foreground font-normal">({g.count} {g.count === 1 ? 'boleto' : 'boletos'})</span>
                          </td>
                          <td className="px-2 py-1.5 text-muted-foreground max-w-[120px] truncate">{g.grupo}</td>
                          <td className="px-2 py-1.5 text-muted-foreground/40">—</td>
                          <td className="px-2 py-1.5 text-muted-foreground">{g.minVenc ? formatDate(g.minVenc) : '—'}</td>
                          <td className="px-2 py-1.5 text-right"><span className={`inline-block px-1 py-0.5 rounded text-[9px] font-bold ${atrasoBg}`}>{g.maxAtraso}d</span></td>
                          <td className="px-2 py-1.5 text-right font-semibold text-red-500">{formatCurrency(g.saldo)}</td>
                          <td className="px-2 py-1.5 text-center text-muted-foreground/30">—</td>
                          <td className="px-2 py-1.5 text-center text-muted-foreground/30">—</td>
                        </tr>
                        {expanded && g.boletos.map(b => renderBoletoRow(b, { child: true }))}
                      </Fragment>
                    )
                  })}
            </tbody>
          </table>
          {filteredBoletos.length === 0 && !isLoading && (
            <div className="flex flex-col items-center py-10 text-muted-foreground">
              <FiSearch className="w-6 h-6 mb-2 opacity-30" />
              <span className="text-xs">Nenhum boleto bate com os filtros atuais</span>
              {hasActiveFilters && (
                <button onClick={clearFilters} className="text-[11px] text-primary hover:underline mt-1">Limpar filtros</button>
              )}
            </div>
          )}
        </div>
      </div>
    )
  }
}

// Componentes auxiliares — declarados fora pra não recriar a cada render do pai.

// (KpiInline saiu em 21/10/2026: os números da carteira viraram PlnKpi no topo da tela.)

interface FilterChipProps { active: boolean; onClick: () => void; children: React.ReactNode }
function FilterChip({ active, onClick, children }: FilterChipProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`h-6 px-2 rounded-md text-[11px] font-medium border transition-colors ${
        active
          ? 'neu-latch bg-primary/10 border-primary/30 text-primary'
          : 'bg-transparent border-border text-muted-foreground hover:bg-secondary hover:text-foreground'
      }`}
    >
      {children}
    </button>
  )
}

// ─────────────────── DossierPane ───────────────────

const TIPO_COLOR: Record<string, string> = {
  '1': 'bg-purple-500/15 text-purple-500', // Empresário
  '2': 'bg-amber-500/15 text-amber-500',   // Líder
  '3': 'bg-blue-500/15 text-blue-500',     // Consultora
  '0': 'bg-slate-500/15 text-slate-500',   // Ger. Distribuição
}

// Cada entrada da timeline É uma cobrança disparada (fato registrado).
// A coluna "status" reflete só o que o Meta reportou sobre entrega — pode ser
// ruído (vimos 487 `failed` no banco contra 2 `read` no device Faturamento,
// mas várias dessas "falharam" foram efetivamente entregues). Por isso a
// label primária mostra "Cobrada" e o status do Meta vira indicador discreto.
const META_STATUS_ICON: Record<string, string> = {
  sent:      '✓',
  delivered: '✓✓',
  read:      '✓✓',
  failed:    '⚠',
  failed_but_responded: '✓✓',
}

const META_STATUS_COLOR: Record<string, string> = {
  sent:      'text-muted-foreground',
  delivered: 'text-sky-500',
  read:      'text-emerald-500',
  failed:    'text-amber-500',
  failed_but_responded: 'text-emerald-500',
}

const META_STATUS_TIP: Record<string, string> = {
  sent:      'Meta aceitou — sem confirmação de entrega',
  delivered: 'Meta confirmou entrega ao WhatsApp do cliente',
  read:      'Cliente abriu/leu a mensagem',
  failed:    'Meta reportou erro — pode ter sido entregue mesmo assim (a flag costuma ser ruidosa). Conferir conversa pra ter certeza.',
  failed_but_responded: 'Meta marcou erro mas o cliente respondeu depois — foi entregue',
}

interface DossierPaneProps {
  loading: boolean
  error: string | null
  data: DossierData | null
  activeDoc: string | null
  boletosAbertos: Boleto[]
  totalAberto: number
  onClose: () => void
  onReload: () => void
  onResendBoleto: (b: Boleto) => void
  isCooldownActive: (b: Boleto) => boolean
  cooldownRemainingLabel: (b: Boleto) => string
  formatRelativeDays: (iso: string) => string
  resendingDoc: string | null
  onOpenConversation?: (conversationId: string) => void
}

function DossierPane(props: DossierPaneProps) {
  const { loading, error, data, activeDoc, boletosAbertos, totalAberto,
          onClose, onReload, onResendBoleto,
          isCooldownActive, cooldownRemainingLabel, formatRelativeDays, resendingDoc,
          onOpenConversation } = props
  const { authenticatedFetch } = useAuth()

  const copyPhone = () => {
    if (!data?.cliente.telefone) return
    navigator.clipboard.writeText(data.cliente.telefone)
  }

  // PLANOS (21/10/2026): gaveta do dossiê — nome do cliente em 16px, seções
  // com título 13px e corpo 12.5–13px (antes 9–11px), respiro px-4, botão h-9.
  return (
    <div className="w-[380px] xl:w-[420px] flex-shrink-0 border-l border-border glass-panel flex flex-col overflow-hidden">
      {/* Header */}
      <div className="neu-bar flex items-center gap-2 px-4 py-3 border-b border-border glass-bar flex-shrink-0">
        <div className="min-w-0 flex-1">
          <div className="text-base font-bold text-foreground truncate uppercase">
            {data?.cliente.nome || (loading ? 'Carregando…' : 'Cliente')}
          </div>
          {data?.cliente.codigo && (
            <div className="flex items-center gap-1.5 mt-1 text-xs text-muted-foreground">
              <span className="font-mono">{data.cliente.codigo}</span>
              {data.cliente.tipoLabel && (
                <span className={`px-1.5 py-0.5 rounded-full ${TIPO_COLOR[data.cliente.tipo] || 'bg-muted text-muted-foreground'}`}>
                  {data.cliente.tipoLabel}
                </span>
              )}
              {data.cliente.situacao && data.cliente.situacao !== 'A' && (
                <span className="neu-relief px-1.5 py-0.5 rounded-full bg-red-500/15 text-red-500">{data.cliente.situacao}</span>
              )}
            </div>
          )}
        </div>
        <button onClick={onClose} className="neu-tab p-2 rounded-lg hover:bg-secondary text-muted-foreground hover:text-foreground" title="Fechar dossier">
          <FiX className="w-4 h-4" />
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto">
        {loading && !data && (
          <div className="flex flex-col items-center justify-center py-10 text-muted-foreground">
            <FiLoader className="w-5 h-5 animate-spin mb-2" />
            <span className="text-[13px]">Carregando dossier...</span>
          </div>
        )}

        {error && (
          <div className="neu-relief m-4 p-3 bg-destructive/10 border border-destructive/30 rounded-lg text-[13px]">
            <div className="flex items-center gap-1.5">
              <FiAlertCircle className="w-3.5 h-3.5 text-destructive flex-shrink-0" />
              <span className="text-destructive flex-1">{error}</span>
              <button onClick={onReload} className="text-primary hover:underline">Tentar de novo</button>
            </div>
          </div>
        )}

        {data && (
          <>
            {/* Quick info */}
            <DossierSection title="Dados">
              {data.cliente.cpf && <DossierRow label="CPF">{formatCpf(data.cliente.cpf)}</DossierRow>}
              {data.cliente.grupo && <DossierRow label="Grupo">{data.cliente.grupo}</DossierRow>}
              {data.cliente.distrito && <DossierRow label="Distrito">{data.cliente.distrito}</DossierRow>}
              {data.cliente.cidade && <DossierRow label="Cidade">{data.cliente.cidade}</DossierRow>}
              {data.cliente.bairro && <DossierRow label="Bairro">{data.cliente.bairro}</DossierRow>}
              {data.cliente.telefoneFormatado && (
                <DossierRow label="Telefone">
                  <button onClick={copyPhone} className="flex items-center gap-1 hover:text-primary transition-colors" title="Copiar">
                    {data.cliente.telefoneFormatado}
                    <FiCopy className="w-3 h-3 opacity-60" />
                  </button>
                </DossierRow>
              )}
              {data.cliente.email && <DossierRow label="Email">{data.cliente.email}</DossierRow>}
              {data.cliente.semLancamento === 1 && (
                <div className="mx-4 mt-1.5 px-3 py-1.5 bg-red-500/10 border-l-2 border-red-500 text-xs text-red-500">
                  Sem lançamento recente
                </div>
              )}
              {!data.cliente.telefone && (
                <div className="mx-4 my-2 px-3 py-2 bg-amber-500/10 border-l-2 border-amber-500 text-xs text-amber-700 dark:text-amber-300 flex items-start gap-1.5">
                  <FiAlertCircle className="w-3 h-3 flex-shrink-0 mt-0.5" />
                  <span>Sem telefone cadastrado no Oracle — cobranças WhatsApp não podem ser disparadas pra este cliente. Atualize o cadastro pra liberar o disparo.</span>
                </div>
              )}
            </DossierSection>

            {/* Status CRM */}
            {data.funnelStatus && (
              <DossierSection title="Status no funil de Cobrança">
                <div className="flex items-center gap-2 px-4 py-2.5">
                  <span
                    className="neu-tag px-2.5 py-0.5 rounded-full text-xs font-medium"
                    style={{
                      background: data.funnelStatus.color ? `color-mix(in srgb, ${data.funnelStatus.color} 15%, transparent)` : 'rgb(245 158 11 / 0.15)',
                      color: data.funnelStatus.color || 'rgb(245 158 11)'
                    }}
                  >
                    {data.funnelStatus.title}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    desde {formatRelativeDays(data.funnelStatus.updatedAt)}
                  </span>
                </div>
              </DossierSection>
            )}

            {/* Boletos abertos */}
            <DossierSection title={`Boletos abertos (${boletosAbertos.length})`} accent={formatCurrency(totalAberto)}>
              <div className="px-4 py-2.5 space-y-2">
                {boletosAbertos.length === 0 ? (
                  <div className="text-[13px] text-muted-foreground italic py-1">Nenhum boleto vencido (atualize o dashboard).</div>
                ) : boletosAbertos.map(b => {
                  const isActive = b.documento === activeDoc
                  const atrasoColor = b.dias_atraso <= 7 ? 'text-amber-500' : b.dias_atraso <= 30 ? 'text-orange-500' : 'text-red-500'
                  const cooldownActive = isCooldownActive(b)
                  const isResending = resendingDoc === b.documento
                  return (
                    <div key={b.documento} className={`px-3 py-2.5 rounded-lg border text-[13px] ${isActive ? 'border-primary/40 bg-primary/5' : 'border-border/60 bg-card/60'}`}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-mono text-muted-foreground">{b.documento}</span>
                        <span className={`font-bold ${atrasoColor}`}>{b.dias_atraso}d</span>
                      </div>
                      <div className="flex items-center justify-between gap-2 mt-0.5">
                        <span className="text-muted-foreground">venc {formatDate(b.vencimento)}</span>
                        <span className="font-semibold text-red-500">{formatCurrency(b.saldo)}</span>
                      </div>
                      {b.cobrado && (
                        <div className="flex items-center justify-between gap-1.5 mt-1 pt-1 border-t border-border/50">
                          <span className="text-xs text-muted-foreground flex items-center gap-1">
                            <FiClock className="w-3 h-3" />
                            {b.cobradoEm ? formatRelativeDays(b.cobradoEm) : '—'}
                            {(b.cobrancasCount || 0) > 1 && <span>· {b.cobrancasCount}x</span>}
                          </span>
                          <button
                            onClick={() => onResendBoleto(b)}
                            disabled={isResending || !!resendingDoc || cooldownActive}
                            title={cooldownActive
                              ? `Cooldown ativo — última cobrança há pouco. Aguarde ${cooldownRemainingLabel(b)} antes de reenviar (regra de 24h pra não constranger o cliente).`
                              : 'Reenviar cobrança deste boleto via WhatsApp'}
                            aria-label={cooldownActive ? `Cooldown, aguarde ${cooldownRemainingLabel(b)}` : 'Reenviar boleto'}
                            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-colors disabled:cursor-not-allowed ${
                              cooldownActive
                                ? 'bg-muted text-muted-foreground/70'
                                : 'bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500/20 disabled:opacity-50'
                            }`}
                          >
                            {isResending
                              ? <FiLoader className="w-3 h-3 animate-spin" />
                              : (cooldownActive ? <FiClock className="w-3 h-3" /> : <FiRefreshCw className="w-3 h-3" />)}
                            {cooldownActive ? cooldownRemainingLabel(b) : 'Reenviar'}
                          </button>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </DossierSection>

            {/* Valor na assessoria (cob.twms), calculado pelo próprio cob — só para ver
                (pedido do diretor, 13/10 e 16/10/2026). Logo abaixo dos boletos. */}
            <HonorariosCob authenticatedFetch={authenticatedFetch} codigo={data?.cliente?.codigo} />

            {/* Pagamentos recentes */}
            {data.pagamentos && data.pagamentos.length > 0 && (
              <DossierSection title={`Pagamentos recentes (${data.pagamentos.length})`} accent={formatCurrency(data.pagamentosTotal || 0)}>
                <div className="px-4 py-2.5 space-y-2">
                  {data.pagamentos.map(p => (
                    <div key={`${p.documento}-${p.serie}`} className="neu-convex px-3 py-2.5 rounded-lg border border-border/60 bg-card/60 text-[13px]">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-mono text-muted-foreground">{p.documento}{p.serie ? `·${p.serie}` : ''}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${p.quitado ? 'bg-emerald-500/15 text-emerald-600' : 'bg-amber-500/15 text-amber-600'}`}>{p.quitado ? 'Quitado' : 'Parcial'}</span>
                      </div>
                      <div className="flex items-center justify-between gap-2 mt-0.5">
                        <span className="text-muted-foreground">pago {formatDate(p.ultPagamento || '')} · venc {formatDate(p.vencimento || '')}</span>
                        <span className="font-semibold text-emerald-600">{formatCurrency(p.valorPago)}</span>
                      </div>
                      {!p.quitado && p.saldo > 0 && (
                        <div className="flex items-center justify-end mt-0.5 text-xs text-muted-foreground">resta {formatCurrency(p.saldo)}</div>
                      )}
                    </div>
                  ))}
                </div>
              </DossierSection>
            )}

            {/* Histórico de cobranças */}
            {(() => {
              const docsUnicos = new Set(data.cobrancas.map(c => c.documento).filter(Boolean)).size
              const subtitulo = data.cobrancas.length > 0
                ? `${data.cobrancas.length} disparo${data.cobrancas.length === 1 ? '' : 's'} · ${docsUnicos} doc${docsUnicos === 1 ? '' : 's'}`
                : undefined
              return (
            <DossierSection title="Histórico de cobranças" accent={subtitulo}>
              {data.cobrancas.length === 0 ? (
                <div className="px-4 py-3 text-[13px] text-muted-foreground italic">Nenhuma cobrança enviada via WhatsApp.</div>
              ) : (
                <div className="px-4 py-2.5 space-y-2.5">
                  {data.cobrancas.map((c, i) => {
                    // Override: se Meta marcou 'failed' mas o cliente respondeu, foi
                    // entregue. Confiar na resposta é mais robusto que no status do Meta.
                    const effectiveStatus = (c.status === 'failed' && c.resposta) ? 'failed_but_responded' : c.status
                    const statusIcon = META_STATUS_ICON[effectiveStatus] || '·'
                    const statusColor = META_STATUS_COLOR[effectiveStatus] || 'text-muted-foreground'
                    const statusTip = META_STATUS_TIP[effectiveStatus] || ''
                    return (
                    <div key={`${c.messageId || c.timestamp}-${i}`} className="text-[13px]">
                      <div className="flex items-center gap-1.5 text-muted-foreground">
                        <FiMessageCircle className="w-3 h-3" />
                        <span className="text-foreground font-medium">Cobrada</span>
                        <span title={new Date(c.timestamp).toLocaleString('pt-BR')}>{formatRelativeDays(c.timestamp)}</span>
                        {c.documento && <span className="font-mono">· doc {c.documento}</span>}
                        <span
                          className={`ml-auto font-semibold cursor-help ${statusColor}`}
                          title={statusTip}
                        >
                          {statusIcon}
                        </span>
                      </div>
                      {c.resposta && (
                        <div className="neu-relief mt-1.5 ml-4 pl-2.5 py-1.5 border-l-2 border-sky-500/40 bg-sky-500/5 rounded-r text-foreground">
                          <div className="text-xs text-muted-foreground">resposta · <span title={new Date(c.resposta.timestamp).toLocaleString('pt-BR')}>{formatRelativeDays(c.resposta.timestamp)}</span></div>
                          <div className="text-[13px] line-clamp-3">{c.resposta.content}</div>
                        </div>
                      )}
                    </div>
                    )
                  })}
                </div>
              )}
            </DossierSection>
            )})()}

            {/* Ações */}
            <div className="p-4 space-y-1.5">
              <button
                onClick={() => data.conversationId && onOpenConversation?.(data.conversationId)}
                disabled={!data.conversationId}
                title={data.conversationId
                  ? 'Abrir a conversa WhatsApp deste cliente no painel de Conversas'
                  : 'Cliente ainda não tem conversa no WhatsApp'}
                className="neu-relief w-full h-9 flex items-center justify-center gap-1.5 px-3 bg-primary/10 hover:bg-primary/20 text-primary rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FiMessageCircle className="w-3.5 h-3.5" />
                Ver conversa
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

function DossierSection({ title, accent, children }: { title: string; accent?: string; children: React.ReactNode }) {
  return (
    <div className="border-b border-border/50">
      <div className="flex items-center justify-between gap-2 px-4 py-2.5 glass-bar">
        <span className="text-[13px] font-semibold text-foreground">{title}</span>
        {accent && <span className="text-[12.5px] font-semibold text-red-500">{accent}</span>}
      </div>
      {children}
    </div>
  )
}

function DossierRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline gap-2 px-4 py-1 text-[13px]">
      <span className="text-muted-foreground w-20 flex-shrink-0">{label}</span>
      <span className="text-foreground flex-1 min-w-0 truncate">{children}</span>
    </div>
  )
}
